import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { priceToString } from '../../../utils';
import styles from './ProductCard.module.scss';
import { ProductCardType } from '../../../blocks/pdpV2/constants';
import freeTruckSvg from '../../../assets/images/svg/free-truck.svg';

const ProductCard = ({
  title,
  description,
  price,
  name,
  id,
  discount = null,
  imageURL,
  badge = null,
  onChange,
  isSelected,
  badgeStyle = 'normal',
  whiteGloveMessage = '',
}) => {
  const { formatMessage, formatNumber } = useIntl();
  const haveDiscount = discount?.centAmount > 0;

  return (
    <div
      className={classnames(styles['product-card-wrapper'], {
        [styles['is-selected']]: isSelected,
      })}
      onClick={() => onChange(id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onChange(id);
        }
      }}
      role="button"
      tabIndex="0"
    >
      <div className={styles['product-card-overlay']}>
        <div className={styles['product-card-overlay-radio-button']}>
          {isSelected ? (
            <div
              className={styles['product-check-mark']}
              data-testid="check-mark"
            />
          ) : (
            <div
              className={styles['product-uncheck-mark']}
              data-testid="check-mark"
            />
          )}
        </div>
        <div className={styles['product-card-overlay-badge']}>
          {badge && (
            <div
              data-testid="badge-element"
              className={classnames({
                [styles['product-badge']]: badgeStyle === 'normal',
                [styles['alternative-badge']]: badgeStyle === 'alternative',
              })}
            >
              {badge}
            </div>
          )}
        </div>
      </div>

      <div className={styles['product-image']}>
        <img src={imageURL} alt={name} />
      </div>
      <div className={styles['product-info']}>
        <div
          className={classnames(styles['product-title'], {
            [styles['product-title-with-badge']]: !!badge,
          })}
        >
          {title}
        </div>
        <div className={styles['product-description']}>{description}</div>
        <div className={styles['product-price-info']}>
          <div
            className={classnames(styles['product-price'], {
              [styles['product-price-line-through']]: haveDiscount,
            })}
          >
            {priceToString({
              price: haveDiscount
                ? {
                  ...price,
                  centAmount: price.centAmount + discount.centAmount,
                }
                : price,
              maximumFractionDigits: 0,
              formatNumber,
              formatMessage,
            })}
          </div>
          {haveDiscount && (
            <div className={styles['product-discounted-price']}>
              {priceToString({
                price,
                maximumFractionDigits: 0,
                formatNumber,
                formatMessage,
              })}
            </div>
          )}
        </div>
        {whiteGloveMessage ? (
          <div className={styles['white-glove-message']}>
            <img src={freeTruckSvg} alt="Free" />
            <span>{whiteGloveMessage}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};
ProductCard.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  price: PropTypes.shape({
    type: PropTypes.string,
    currencyCode: PropTypes.string,
    centAmount: PropTypes.number.isRequired,
    fractionDigits: PropTypes.number.isRequired,
  }).isRequired,
  discount: PropTypes.shape({
    type: PropTypes.string,
    currencyCode: PropTypes.string,
    centAmount: PropTypes.number,
    fractionDigits: PropTypes.number,
  }),
  imageURL: PropTypes.string.isRequired,
  badge: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  badgeStyle: PropTypes.oneOf([
    ProductCardType.NORMAL,
    ProductCardType.ALTERNATIVE,
  ]),
  whiteGloveMessage: PropTypes.string,
};

export default ProductCard;
